<template>
  <div
    class="form-control password-input"
    :class="{ 'in-focus': focus, 'is-hidden': hidden, 'is-disabled': disabled }"
  >
    <input
      ref="inputEl"
      :autocomplete="autocomplete"
      :name="name"
      :readonly="preventAutofill && !focus"
      :type="hidden ? 'password' : 'text'"
      :value="displayValue"
      :disabled="disabled"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
    />
    <span
      v-if="showEye"
      class="password-toggle"
      role="button"
      :tabindex="disabled ? -1 : 0"
      :title="hidden ? t('showPassword') : t('hidePassword')"
      @mousedown.prevent
      @pointerdown.prevent
      @click="toggleHidden"
      @keydown.enter.space.prevent="toggleHidden"
    >
      <i class="bi" :class="hidden ? 'bi-eye' : 'bi-eye-slash'"></i>
    </span>
  </div>
</template>
<script setup>
import { computed, nextTick, shallowRef, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const PLACEHOLDER_DOTS = '••••••••'

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** Существующая запись: при пустом поле показываем плейсхолдер из точек */
  hasStoredPassword: { type: Boolean, default: false },
  autocomplete: { type: String, default: 'new-password' },
  name: { type: String, default: undefined },
})
const model = defineModel({ default: '' })

const { t } = useI18n()
const inputEl = ref(null)
const hidden = shallowRef(true)
const focus = shallowRef(false)

const preventAutofill = computed(() => props.autocomplete === 'new-password')
const isStoredMaskMode = computed(() => props.hasStoredPassword && !focus.value)
const displayValue = computed(() =>
  isStoredMaskMode.value ? PLACEHOLDER_DOTS : String(model.value ?? ''),
)
const showEye = computed(() => focus.value && String(model.value ?? '').length > 0)

function onInput(e) {
  if (props.disabled) return
  model.value = String(e?.target?.value ?? '')
}

async function onFocus() {
  if (props.disabled) return
  if (preventAutofill.value) inputEl.value?.removeAttribute('readonly')
  focus.value = true
  hidden.value = true
  if (props.hasStoredPassword && !String(model.value ?? '').length) {
    await nextTick()
    if (inputEl.value) inputEl.value.value = ''
  }
}

function onBlur() {
  focus.value = false
  hidden.value = true
}

function toggleHidden() {
  if (props.disabled) return
  hidden.value = !hidden.value
}

defineExpose({
  getRawValue: () => String(inputEl.value?.value ?? model.value ?? ''),
})
</script>
<style scoped>
.password-toggle {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  cursor: pointer;
  user-select: none;
}
.password-toggle i {
  font-size: 1.1rem;
}
.password-input.is-disabled .password-toggle {
  pointer-events: none;
  opacity: 0.6;
  cursor: default;
}
</style>

<i18n>
{
  "ru": {
    "showPassword": "Показать пароль",
    "hidePassword": "Скрыть пароль"
  },
  "en": {
    "showPassword": "Show Password",
    "hidePassword": "Hide Password"
  }
}
</i18n>
